import React, { useEffect, useState } from "react";
import { Box, Typography, Button, TextField, Paper, Snackbar, Alert } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MapComponent from "../MapComponent";
import { IOrderListDTO, IOrderViewResponseDTO, IMarkItemPackedResponseDTO } from "../../interfaces/Orders";
import { getPackageOrderDetails, scanPackBarCode, packorder } from "../../services/Orders";

// const ROUTE_STEPS = [
//   { id: 1, name: "Warehouse Hub", address: "123 Industrial Area, Delhi", date: "14/01/2026", completed: true },
//   { id: 2, name: "Distribution Center", address: "45 Main Road, Delhi", date: "14/01/2026", completed: true },
//   { id: 3, name: "Local Hub", address: "78 Sector 12, Delhi", date: "15/01/2026", completed: false },
//   { id: 4, name: "Customer Location", address: "32, Karol Bagh, Delhi, New Delhi", date: "15/01/2026", completed: false, isDestination: true },
// ];

interface OrderPackagingProps {
  orderData: IOrderListDTO | null;
  onBack: () => void;
  onSave: () => void;
}

const OrderPackaging =({ orderData, onBack, onSave }: OrderPackagingProps) => {
  const [skuInput, setSKUInput] = useState("");
  const [orderDetails, setorderDetails] = useState<IOrderViewResponseDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  useEffect(() => {
    if (!orderData?.orderId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await getPackageOrderDetails(orderData.orderId);
        if (res) {
          setorderDetails(res as IOrderViewResponseDTO);
        }
      } catch {
        setorderDetails(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderData?.orderId]);
  const routeDetails = [
    { name: "Warehouse", address: orderDetails?.warehouseAddress || "", type: "start" },
    { name: orderDetails?.customerName || "Customer", address: orderDetails?.address || "", type: "end" },
  ];
  const handleBarcodeScan = async () => {
    if (!skuInput.trim() || !orderData?.orderId) return;
    setError("");
    setScanning(true);
    try {
      const response = await scanPackBarCode(orderData.orderId, { barcode: skuInput.trim() });
      if (response?.status === "success" && response?.rawResponse?.data) {
        const res: IMarkItemPackedResponseDTO = response.rawResponse.data;
        if (res.packed && orderDetails) {
          setorderDetails({
            ...orderDetails,
            packedItems: res.packedItems,
            totalItems: res.totalItems,
            packedPercentage: res.totalItems > 0 ? Math.round((res.packedItems / res.totalItems) * 100) : 0,
            items: orderDetails.items.map((item) =>
              item.orderItemId === res.orderItemId ? { ...item, packed: true } : item
            ),
          });
        }
        setSKUInput("");
      } else {
        setError(response?.rawResponse?.data?.message || "Item not found or already packed.");
      }
    } catch {
      setError("Error scanning barcode.");
    } finally {
      setScanning(false);
    }
  };

  const allItemsPacked = orderDetails?.items?.every((item) => item.packed) ?? false;

  const handlePackOrder = async () => {
    if (!orderData?.orderId) return;
    setLoading(true);
    try {
      const res = await packorder(orderData.orderId);
      if (res && res.status === "success") {
        setOpenSuccess(true);
        setMsg("Order Packed Successfully.");
        onSave();
        onBack();
      } else {
        setOpenError(true);
        setMsg("Failed to pack Order.");
      }
    } catch {
      setOpenError(true);
      setMsg("Unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => {
    setOpenSuccess(false);
    setOpenError(false);
    setMsg("");
  };

  const completionPercentage = orderDetails?.packedPercentage ?? 0;

  const origin = orderDetails?.warehouseLatitude && orderDetails?.warehouseLongitude
    ? { lat: orderDetails.warehouseLatitude, lng: orderDetails.warehouseLongitude } : undefined;
  const destination = orderDetails?.customerLatitude && orderDetails?.customerLongitude
    ? { lat: orderDetails.customerLatitude, lng: orderDetails.customerLongitude } : undefined;

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#F5F5F5" }}>
      <Box sx={{ p: 1 }}>
        <Button startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />} onClick={onBack} sx={{ textTransform: "none", color: "text.primary", p: 0, minWidth: "auto", fontSize: "14px" }}>
          Back
        </Button>
      </Box>

      {/* Paper Container */}
      <Paper sx={{ flex: 1, m: 1, mt: 0, display: "flex", flexDirection: "column", borderRadius: "8px", overflow: "hidden" }}>
        {/* Header - Fixed */}
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Order Packaging</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>Scan the Barcode to add item in package.</Typography>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          <Box sx={{ p: 3 }}>
            {/* Order Details Box */}
            <Paper sx={{ p: 2.5, mb: 3, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none" }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Order Details</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>Order ID</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "13px" }}>{orderDetails?.orderId??""}</Typography>
                </Box>
                <Box sx={{ height: "30px", width: "1px", backgroundColor: "grey.light" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>Customer Name</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "13px" }}>{orderDetails?.customerName??""}</Typography>
                </Box>
                <Box sx={{ height: "30px", width: "1px", backgroundColor: "grey.light" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>Phone Number</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "13px" }}>{orderDetails?.phone??""}</Typography>
                </Box>
                <Box sx={{ height: "30px", width: "1px", backgroundColor: "grey.light" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>Pincode</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: "13px" }}>{orderDetails?.pincode??""}</Typography>
                </Box>
                <Box sx={{ height: "30px", width: "1px", backgroundColor: "grey.light" }} />
                 <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>Address</Typography>
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: "13px" }}>{orderDetails?.address??""}</Typography>
              </Box>
              </Box>
            </Paper>

            {/* Two Column Layout */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              {/* Left Section */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* Enter Barcode */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Barcode or Scan"
                  value={skuInput}
                  onChange={(e) => { setSKUInput(e.target.value); setError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleBarcodeScan(); }}
                  disabled={scanning}
                  error={!!error}
                  helperText={error}
                  sx={{
                    mb: 0.5,
                    backgroundColor: "#F9FAFB",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      "& fieldset": { borderColor: error ? "error.main" : "rgba(74,85,101,0.15)" },
                      "&:hover fieldset": { borderColor: "#000" },
                      "&.Mui-focused fieldset": { borderColor: "#000" },
                    },
                    "& .MuiInputBase-input": { fontSize: "12px", padding: "10px 12px" },
                  }}
                />

                {/* Packing Checklist */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "14px" }}>Packing Checklist</Typography>
                  <Typography variant="body2" sx={{ color: "success.main", fontSize: "12px", fontWeight: 600 }}>{completionPercentage}% Complete</Typography>
                </Box>

                {/* Items List */}
                <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none", flex: 1 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {orderDetails?.items?.map((item) => (
                      <Box
                      key={item.orderItemId}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: "8px",
                          backgroundColor: item.packed ? "success.light" : "#F9FAFB",
                          border: "1px solid",
                          borderColor: item.packed ? "rgba(0, 178, 33, 0.2)" : "rgba(74,85,101,0.15)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 45,
                            height: 45,
                            borderRadius: "6px",
                            backgroundColor: "#E5E7EB",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Box component="img" src={item.imageUrl || ""} alt={item.itemName??""} sx={{ width: 35, height: 35, objectFit: "contain" }} onError={(e: any) => { e.target.style.display = "none"; }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{item.itemName??""}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>SKU : {item.barcode??""}</Typography>
                        </Box>
                        {item.packed ? (
                          <CheckCircleIcon sx={{ color: "success.main", fontSize: "22px" }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ color: "grey.primary", fontSize: "22px" }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>

              {/* Right Section */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* Map Overview */}
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontSize: "14px" }}>Map Overview</Typography>
                <Box sx={{ width: "100%", height: "350px", borderRadius: "8px", overflow: "hidden", mb: 3 }}>
                  <MapComponent height="100%" zoom={12} origin={origin} destination={destination} />
                </Box>

                {/* Route Details */}
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb:1, fontSize: "14px" }}>Route Details</Typography>
                <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none", flex: 1 }}>
                  <Box sx={{ position: "relative" }}>
                  {routeDetails.map((route, index) => (
                          <Box key={index} sx={{ display: "flex", gap: 2, pb: index < routeDetails.length - 1 ? 3 : 0 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                              <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "white", border: "3px solid", borderColor: route.type === "start" ? "primary.main" : "error.main", zIndex: 1 }} />
                              {index < routeDetails.length - 1 && <Box sx={{ width: 3, flex: 1, backgroundColor: "primary.main", position: "absolute", top: 24, bottom: -24 }} />}
                            </Box>
                            <Box sx={{ flex: 1, pb: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{route.name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px", display: "block" }}>{route.address}</Typography>
                            </Box>
                          </Box>
                        ))}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer - Fixed */}
        <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, backgroundColor: "#fff", borderTop: "1px solid", borderColor: "grey.100" }}>
          <Button 
            variant="outlined" 
            onClick={onBack} 
            sx={{ 
              borderRadius: "6px", 
              border: "1px solid", 
              borderColor: "primary.main", 
              boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", 
              padding: "8px 16px", 
              textTransform: "none",
              "&:hover": {
                border: "1px solid",
                borderColor: "primary.main",
                boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            disabled={!allItemsPacked || loading}
            onClick={handlePackOrder}
            sx={{ 
              borderRadius: "6px", 
              border: "1px solid", 
              borderColor: "primary.main", 
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", 
              padding: "8px 40px", 
              textTransform: "none", 
              "&:hover": { 
                backgroundColor: "primary.main", 
                boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" 
              } 
            }}
          >
            Pack Order
          </Button>
        </Box>
      </Paper>

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackClose}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openSuccess} autoHideDuration={5000} onClose={handleSnackClose}>
        <Alert severity="success" variant="filled" sx={{ width: "100%" }}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
};
export default OrderPackaging;
